import { range } from "client_legacy/ndaw/arrayHelpers";
import { ReactElement, useMemo } from "react";
import { Icon, IconSize } from "../Icon/Icon";
import { PagerBase, PagerBaseAlign } from "./PagerBase";
import { PagerSelectOption } from "./PagerSelect";
import { localize } from "client_legacy/ndaw/culture";

export type PagerProps = {
	pageNumber: number;
	pagesCount: number;
	onNewPageNumber(newPageNumber: number): void;
	alignment?: PagerBaseAlign;
	trackingId?: string;
};

export const Pager = (props: PagerProps): ReactElement => {
	const {
		pageNumber,
		pagesCount,
		onNewPageNumber,
		alignment,
		trackingId
	} = props;

	const MAX_NUMBER_OF_PAGES_TO_RENDER = 7;
	const firstPageNumberToRender: number = Math.floor(pageNumber / MAX_NUMBER_OF_PAGES_TO_RENDER) * MAX_NUMBER_OF_PAGES_TO_RENDER;
	const shouldRenderLeftDots: boolean = firstPageNumberToRender > 0;
	const lastPageNumberToRender: number = Math.min(firstPageNumberToRender + MAX_NUMBER_OF_PAGES_TO_RENDER, pagesCount);
	const shouldRenderRightDots: boolean = lastPageNumberToRender < pagesCount;
	const pagesToRender: number[] = [];

	for (let i = firstPageNumberToRender; i < lastPageNumberToRender; i++) {
		pagesToRender.push(i);
	}

	const selectOptionsToRender = useMemo(
		(): readonly PagerSelectOption[] => {
			return range(0, pagesCount)
				.map(n => ({
					value: n,
					label: (n + 1).toString()
				}));
		},
		[pagesCount]
	);

	return (
		<PagerBase
			alignment={alignment}
			trackingId={trackingId}
			ariaLabel={localize("_Pagination")}
		>
			<PagerBase.Button
				kind="arrow"
				onClick={() => onNewPageNumber(0)}
				isDisabled={pageNumber <= 0 || pagesCount <= 0}
				trackingId="go-to-beginning"
				title={localize("_First")}
			>
				<Icon
					value="arrow-left-to-line"
					size={iconSize}
				/>
			</PagerBase.Button>
			<PagerBase.Button
				kind="arrow"
				onClick={() => onNewPageNumber(pageNumber - 1)}
				isDisabled={pageNumber <= 0 || pagesCount <= 0}
				trackingId="go-to-previous"
				title={localize("_Previous")}
			>
				<Icon
					value="chevron-left"
					size={iconSize}
				/>
			</PagerBase.Button>

			{shouldRenderLeftDots && (
				<PagerBase.Button
					kind="page"
					onClick={() => onNewPageNumber(firstPageNumberToRender - 1)}
					trackingId="first-skipped-range"
					title={localize("_JumpToPreviousPages")}
				>
					<Icon
						value="ellipsis"
						size={iconSize}
					/>
				</PagerBase.Button>
			)}

			{pagesToRender.map(n => (
				<PagerBase.Button
					key={n}
					kind="page"
					onClick={() => onNewPageNumber(n)}
					isActive={n === pageNumber}
					title={`${n + 1}`}
					trackingId={`go-to-page-${n}`}
				>
					{n + 1}
				</PagerBase.Button>
			))}

			{shouldRenderRightDots && (
				<PagerBase.Button
					kind="page"
					onClick={() => onNewPageNumber(lastPageNumberToRender)}
					trackingId="second-skipped-range"
					title={localize("_JumpToNextPages")}
				>
					<Icon
						value="ellipsis"
						size={iconSize}
					/>
				</PagerBase.Button>
			)}

			{selectOptionsToRender.length > 0 && (
				<PagerBase.Select
					value={pageNumber}
					onChange={newValue => onNewPageNumber(newValue)}
					options={selectOptionsToRender}
					trackingId="mobile-select"
				/>
			)}

			<PagerBase.Button
				kind="arrow"
				onClick={() => onNewPageNumber(pageNumber + 1)}
				isDisabled={pageNumber >= pagesCount - 1 || pagesCount <= 0}
				trackingId="go-to-next"
				title={localize("_Next")}
			>
				<Icon
					value="chevron-right"
					size={iconSize}
				/>
			</PagerBase.Button>
			<PagerBase.Button
				kind="arrow"
				onClick={() => onNewPageNumber(pagesCount - 1)}
				isDisabled={pageNumber >= pagesCount - 1 || pagesCount <= 0}
				trackingId="go-to-end"
				title={localize("_Last")}
			>
				<Icon
					value="arrow-right-to-line"
					size={iconSize}
				/>
			</PagerBase.Button>
		</PagerBase>
	);
};

Pager.displayName = "Pager";

const iconSize: IconSize = "large";
